// @oasis-permission-inherits: /capcode (capcode:view)
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Play } from "lucide-react";
import * as React from "react";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import { testAutomation } from "~/modules/cap-code/network";

export const Route = createFileRoute("/_dash/capcode/automations/new")({
  component: NewAutomationRoute,
});

function detectedTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
}

function NewAutomationRoute() {
  const navigate = useNavigate({ from: Route.fullPath });
  const [title, setTitle] = React.useState("");
  const [scheduleText, setScheduleText] = React.useState("");
  const timezone = React.useMemo(detectedTimezone, []);
  const [prompt, setPrompt] = React.useState("");
  const [parseError, setParseError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const runTest = async () => {
    setBusy("test");
    setError(null);
    setParseError(null);
    try {
      const result = await testAutomation({
        title: title.trim() || undefined,
        prompt,
        scheduleText,
        timezone,
      });
      if ("supported" in result) {
        if (result.supported) {
          setError("Schedule parsed, but no test run was created.");
        } else {
          setParseError(result.error);
        }
        return;
      }
      navigate({
        to: "/capcode/automations/$automationId",
        params: { automationId: result.automation.id },
      });
    } catch {
      setError("Failed to run automation test.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="h-[calc(100dvh-57px)] overflow-y-auto bg-background">
      <div className="mx-auto grid w-full max-w-6xl gap-5 px-4 py-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <main className="space-y-5">
          <header>
            <h1 className="text-2xl font-semibold tracking-tight">New Automation</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Test the prompt once, then save it on a recurring schedule.
            </p>
          </header>

          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="space-y-4 rounded-lg border border-border bg-white p-4">
            <div className="space-y-2">
              <Label htmlFor="automation-title">Title</Label>
              <Input
                id="automation-title"
                value={title}
                placeholder="Studio feedback summary"
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="grid gap-4 md:grid-cols-[1fr_220px]">
              <div className="space-y-2">
                <Label htmlFor="automation-schedule">Schedule</Label>
                <Input
                  id="automation-schedule"
                  value={scheduleText}
                  placeholder="Every day at 10am"
                  onChange={(e) => {
                    setScheduleText(e.target.value);
                    setParseError(null);
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label>Timezone</Label>
                <div className="flex h-10 items-center rounded-md border border-border bg-muted/30 px-3 text-sm text-muted-foreground">
                  {timezone}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="automation-prompt">Prompt</Label>
              <Textarea
                id="automation-prompt"
                className="min-h-44"
                value={prompt}
                placeholder="Look at a Slack feedback channel, summarize poor feedback, and send me a Slack message when there is something important."
                onChange={(e) => setPrompt(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => void runTest()}
                disabled={busy != null || !prompt.trim() || !scheduleText.trim()}
              >
                <Play size={16} />
                Test
              </Button>
            </div>
          </div>
        </main>

        <aside className="space-y-4">
          {parseError && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              <h2 className="font-semibold">Schedule not understood</h2>
              <p className="mt-1">{parseError}</p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
